import { useState, useEffect, useCallback } from 'react';
import { Paper, Title, Text, Group, Stack, Select, Badge, SimpleGrid, RingProgress, ThemeIcon, Loader, Center, Button } from '@mantine/core';
import { IconCpu, IconServer, IconDatabase, IconRefresh } from '@tabler/icons-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { useTranslation } from 'react-i18next';

export default function AdminVpsMonitorTab({ user, apiBaseUrl }) {
    const { t } = useTranslation();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [range, setRange] = useState('24h');
    const [latest, setLatest] = useState(null);

    const rangeOptions = [
        { value: '1h', label: t('vpsMonitor.range1h') },
        { value: '6h', label: t('vpsMonitor.range6h') },
        { value: '24h', label: t('vpsMonitor.range24h') },
        { value: '7d', label: t('vpsMonitor.range7d') },
        { value: '30d', label: t('vpsMonitor.range30d') },
    ];

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const now = new Date();
            const start = new Date();
            switch (range) {
                case '1h': start.setHours(now.getHours() - 1); break;
                case '6h': start.setHours(now.getHours() - 6); break;
                case '24h': start.setDate(now.getDate() - 1); break;
                case '7d': start.setDate(now.getDate() - 7); break;
                case '30d': start.setDate(now.getDate() - 30); break;
            }
            const params = new URLSearchParams({
                ownerEmail: user?.email || 'office@ailinkb.com',
                startDate: start.toISOString(),
                endDate: now.toISOString()
            });
            const res = await fetch(`${apiBaseUrl}/api/reports/system-resources?${params}`);
            const json = await res.json();

            const processed = json.map(item => {
                const ramPercent = item.totalRamBytes > 0 ? (item.usedRamBytes / item.totalRamBytes * 100) : 0;
                const diskPercent = item.totalDiskBytes > 0 ? (item.usedDiskBytes / item.totalDiskBytes * 100) : 0;
                const time = new Date(item.timestamp);
                return {
                    time: time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    fullTime: time.toLocaleString(),
                    cpu: Math.round(item.cpuUsagePercent * 10) / 10,
                    ram: Math.round(ramPercent * 10) / 10,
                    disk: Math.round(diskPercent * 10) / 10,
                    ramUsedGb: (item.usedRamBytes / 1073741824).toFixed(1),
                    ramTotalGb: (item.totalRamBytes / 1073741824).toFixed(1),
                    diskUsedGb: (item.usedDiskBytes / 1073741824).toFixed(1),
                    diskTotalGb: (item.totalDiskBytes / 1073741824).toFixed(1),
                };
            });

            // Downsample if too many points
            let sampled = processed;
            if (processed.length > 200) {
                const step = Math.ceil(processed.length / 200);
                sampled = processed.filter((_, i) => i % step === 0);
            }

            setData(sampled);
            if (sampled.length > 0) setLatest(sampled[sampled.length - 1]);
        } catch (e) {
            console.error('Failed to fetch VPS metrics:', e);
        } finally {
            setLoading(false);
        }
    }, [range, user, apiBaseUrl]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const getStatusColor = (value) => {
        if (value >= 90) return 'red';
        if (value >= 70) return 'yellow';
        return 'green';
    };

    const CustomTooltip = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const d = payload[0].payload;
        return (
            <Paper p="sm" shadow="lg" radius="md" style={{ background: 'rgba(30,30,50,0.95)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <Text size="xs" c="dimmed">{d.fullTime}</Text>
                {payload.map((p, i) => (
                    <Text key={i} size="sm" fw={600} c={p.color}>{p.name}: {p.value}%</Text>
                ))}
                {d.ramUsedGb && <Text size="xs" c="dimmed">RAM: {d.ramUsedGb}/{d.ramTotalGb} GB</Text>}
                {d.diskUsedGb && <Text size="xs" c="dimmed">{t('vpsMonitor.disk')}: {d.diskUsedGb}/{d.diskTotalGb} GB</Text>}
            </Paper>
        );
    };

    const StatCard = ({ icon: Icon, color, label, value, unit, percent }) => (
        <Paper p="lg" radius="lg" style={{
            background: 'linear-gradient(135deg, rgba(30,30,60,0.8), rgba(40,40,80,0.6))',
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(12px)',
        }}>
            <Group justify="space-between" mb="xs">
                <Group gap="xs">
                    <ThemeIcon variant="light" color={color} size="lg" radius="md">
                        <Icon size={20} />
                    </ThemeIcon>
                    <Text size="sm" c="dimmed" fw={500}>{label}</Text>
                </Group>
                <Badge color={getStatusColor(percent)} variant="light" size="lg">{percent}%</Badge>
            </Group>
            <Text size="xl" fw={700} c="white">{value} <Text span size="sm" c="dimmed">{unit}</Text></Text>
            <RingProgress
                size={60}
                thickness={6}
                roundCaps
                sections={[{ value: percent, color: getStatusColor(percent) }]}
                mt="xs"
            />
        </Paper>
    );

    const ChartSection = ({ title, dataKey, gradientId, color1, color2 }) => (
        <Paper p="lg" radius="lg" mt="md" style={{
            background: 'linear-gradient(135deg, rgba(30,30,60,0.6), rgba(20,20,50,0.8))',
            border: '1px solid rgba(255,255,255,0.06)',
        }}>
            <Title order={5} c="white" mb="sm">{title}</Title>
            <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <defs>
                        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={color1} stopOpacity={0.4} />
                            <stop offset="95%" stopColor={color2} stopOpacity={0.05} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="time" tick={{ fill: '#888', fontSize: 11 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} />
                    <YAxis domain={[0, 100]} tick={{ fill: '#888', fontSize: 11 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} unit="%" />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceLine y={90} stroke="#ff4444" strokeDasharray="8 4" strokeWidth={2} label={{ value: '90% Alert', fill: '#ff4444', fontSize: 11 }} />
                    <Area type="monotone" dataKey={dataKey} stroke={color1} fill={`url(#${gradientId})`} strokeWidth={2} name={title} dot={false} activeDot={{ r: 4, fill: color1 }} />
                </AreaChart>
            </ResponsiveContainer>
        </Paper>
    );

    if (loading && data.length === 0) {
        return <Center h={400}><Loader color="indigo" size="lg" /></Center>;
    }

    return (
        <Stack gap="md">
            <Group justify="space-between">
                <Title order={3} c="white">{t('vpsMonitor.title')}</Title>
                <Group>
                    <Select
                        value={range}
                        onChange={setRange}
                        data={rangeOptions}
                        w={200}
                        radius="md"
                    />
                    <Button 
                        variant="light" 
                        leftSection={<IconRefresh size={16} />} 
                        onClick={fetchData} 
                        loading={loading}
                        color="indigo"
                    >
                        Odśwież
                    </Button>
                </Group>
            </Group>

            {latest && (
                <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                    <StatCard icon={IconCpu} color="blue" label={t('vpsMonitor.cpu')} value={latest.cpu} unit="%" percent={latest.cpu} />
                    <StatCard icon={IconServer} color="violet" label={t('vpsMonitor.ram')} value={`${latest.ramUsedGb}/${latest.ramTotalGb}`} unit="GB" percent={latest.ram} />
                    <StatCard icon={IconDatabase} color="orange" label={t('vpsMonitor.disk')} value={`${latest.diskUsedGb}/${latest.diskTotalGb}`} unit="GB" percent={latest.disk} />
                </SimpleGrid>
            )}

            <ChartSection title={t('vpsMonitor.cpuChart')} dataKey="cpu" gradientId="cpuGrad" color1="#4dabf7" color2="#228be6" />
            <ChartSection title={t('vpsMonitor.ramChart')} dataKey="ram" gradientId="ramGrad" color1="#9775fa" color2="#7048e8" />
            <ChartSection title={t('vpsMonitor.diskChart')} dataKey="disk" gradientId="diskGrad" color1="#ffa94d" color2="#fd7e14" />
        </Stack>
    );
}
